import React, { useCallback, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SofaIcon, BedIcon, UtensilsIcon, BathIcon, ShirtIcon, AirVentIcon, DoorOpenIcon, LayoutGridIcon, ArrowLeftIcon } from 'lucide-react';
// Components
import { LoginScreen } from './components/LoginScreen';
import { MobileHeader } from './components/MobileHeader';
import { SensorListView } from './components/SensorListView';
import { AlertsSheet } from './components/AlertsSheet';
import { SensorDetailSidebar } from './components/SensorDetailSidebar';
import { Sensor, MOCK_SENSORS, FloorPlanHeatMap } from './components/FloorPlanHeatMap';
// ─── Types ───
interface EditorSensor {
  id: string;
  type: 'SEO' | 'SEL' | 'SES' | 'RADAR';
  label: string;
  x: number;
  y: number;
  active: boolean;
}
interface EditorZone {
  id: string;
  name: string;
  top: number;
  left: number;
  width: number;
  height: number;
  sensors: EditorSensor[];
}
// ─── Data ───
const INITIAL_EDITOR_ZONES: EditorZone[] = [{
  id: 'living',
  name: '거실',
  top: 48.1,
  left: 34.6,
  width: 35.9,
  height: 45.3,
  sensors: [{
    id: 'seo-living-1',
    type: 'SEO',
    label: 'SEO-01',
    x: 30,
    y: 40,
    active: true
  }, {
    id: 'sel-living-1',
    type: 'SEL',
    label: 'SEL-01',
    x: 60,
    y: 25,
    active: true
  }, {
    id: 'ses-living-1',
    type: 'SES',
    label: 'SES-01',
    x: 85,
    y: 70,
    active: false
  }]
}, {
  id: 'bedroom',
  name: '침실',
  top: 55.3,
  left: 5.8,
  width: 27.5,
  height: 25.1,
  sensors: [{
    id: 'seo-bedroom-1',
    type: 'SEO',
    label: 'SEO-02',
    x: 25,
    y: 50,
    active: true
  }, {
    id: 'sel-bedroom-1',
    type: 'SEL',
    label: 'SEL-02',
    x: 50,
    y: 30,
    active: true
  }, {
    id: 'ses-bedroom-1',
    type: 'SES',
    label: 'SES-02',
    x: 80,
    y: 60,
    active: true
  }, {
    id: 'radar-bedroom-1',
    type: 'RADAR',
    label: 'radar-1',
    x: 50,
    y: 80,
    active: true
  }]
}, {
  id: 'bedroom2',
  name: '침실2',
  top: 15.0,
  left: 19.1,
  width: 17.9,
  height: 27.4,
  sensors: [{
    id: 'sel-bedroom2-1',
    type: 'SEL',
    label: 'SEL-05',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'master_bedroom',
  name: '안방',
  top: 57.4,
  left: 72.0,
  width: 24.7,
  height: 31.3,
  sensors: [{
    id: 'seo-master-1',
    type: 'SEO',
    label: 'SEO-05',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'kitchen',
  name: '주방',
  top: 14.5,
  left: 38.6,
  width: 22.7,
  height: 29.9,
  sensors: [{
    id: 'seo-kitchen-1',
    type: 'SEO',
    label: 'SEO-03',
    x: 35,
    y: 45,
    active: true
  }, {
    id: 'sel-kitchen-1',
    type: 'SEL',
    label: 'SEL-03',
    x: 65,
    y: 30,
    active: false
  }, {
    id: 'ses-kitchen-1',
    type: 'SES',
    label: 'SES-03',
    x: 20,
    y: 70,
    active: true
  }]
}, {
  id: 'bathroom',
  name: '화장실',
  top: 46.2,
  left: 80.7,
  width: 15.8,
  height: 9.6,
  sensors: [{
    id: 'seo-bathroom-1',
    type: 'SEO',
    label: 'SEO-04',
    x: 40,
    y: 40,
    active: true
  }, {
    id: 'sel-bathroom-1',
    type: 'SEL',
    label: 'SEL-04',
    x: 60,
    y: 60,
    active: true
  }]
}, {
  id: 'bathroom2',
  name: '화장실2',
  top: 28.8,
  left: 5.8,
  width: 11.9,
  height: 14.7,
  sensors: [{
    id: 'ses-bathroom2-1',
    type: 'SES',
    label: 'SES-04',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'dressroom',
  name: '드레스룸',
  top: 44.5,
  left: 5.9,
  width: 19.5,
  height: 8.9,
  sensors: [{
    id: 'sel-dressroom-1',
    type: 'SEL',
    label: 'SEL-06',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'outdoor_unit',
  name: '실외기실',
  top: 11.2,
  left: 6.1,
  width: 11.3,
  height: 16.8,
  sensors: [{
    id: 'seo-outdoor-1',
    type: 'SEO',
    label: 'SEO-06',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'entrance',
  name: '현관',
  top: 34.5,
  left: 67.7,
  width: 12.0,
  height: 22.2,
  sensors: [{
    id: 'ses-entrance-1',
    type: 'SES',
    label: 'SES-05',
    x: 50,
    y: 50,
    active: true
  }]
}, {
  id: 'alpha_room',
  name: '알파룸',
  top: 8.6,
  left: 36.4,
  width: 11.6,
  height: 11.6,
  sensors: [{
    id: 'sel-alpha-1',
    type: 'SEL',
    label: 'SEL-07',
    x: 50,
    y: 50,
    active: false
  }]
}];
const ROOMS = [{
  id: 'living',
  name: '거실',
  temp: 24,
  icon: SofaIcon
}, {
  id: 'bedroom',
  name: '침실',
  temp: 27,
  icon: BedIcon
}, {
  id: 'bedroom2',
  name: '침실2',
  temp: 25,
  icon: BedIcon
}, {
  id: 'master_bedroom',
  name: '안방',
  temp: 26,
  icon: BedIcon
}, {
  id: 'kitchen',
  name: '주방',
  temp: 22,
  icon: UtensilsIcon
}, {
  id: 'bathroom',
  name: '화장실',
  temp: 21,
  icon: BathIcon
}, {
  id: 'bathroom2',
  name: '화장실2',
  temp: 22,
  icon: BathIcon
}, {
  id: 'dressroom',
  name: '드레스룸',
  temp: 23,
  icon: ShirtIcon
}, {
  id: 'outdoor_unit',
  name: '실외기실',
  temp: 28,
  icon: AirVentIcon
}, {
  id: 'entrance',
  name: '현관',
  temp: 22,
  icon: DoorOpenIcon
}, {
  id: 'alpha_room',
  name: '알파룸',
  temp: 24,
  icon: LayoutGridIcon
}];
const FLOOR_PLAN_URL = 'https://iot.seqsys.net/api/images/public/VhhX6m93VQ03Pw5HhZC5oAOQcjFABApg';
// ─── View Types ───
type AppView = 'login' | 'main' | 'sensorDetail' | 'floorPlan';
// Slide direction for transitions
type SlideDirection = 'left' | 'right';
const slideVariants = {
  enterFromRight: {
    x: '100%',
    opacity: 0
  },
  enterFromLeft: {
    x: '-100%',
    opacity: 0
  },
  center: {
    x: 0,
    opacity: 1
  },
  exitToRight: {
    x: '100%',
    opacity: 0
  },
  exitToLeft: {
    x: '-100%',
    opacity: 0
  }
};
export function App() {
  // Auth
  const [authToken, setAuthToken] = useState<string | null>(null);
  // Navigation
  const [currentView, setCurrentView] = useState<AppView>('login');
  const [slideDirection, setSlideDirection] = useState<SlideDirection>('left');
  // Zone / Sensor
  const [selectedRoomId, setSelectedRoomId] = useState('living');
  const [selectedSensor, setSelectedSensor] = useState<Sensor | null>(null);
  // Alerts
  const [showAlerts, setShowAlerts] = useState(false);
  // Floor plan
  const [viewMode, setViewMode] = useState<'energy' | 'comfort' | 'airquality' | 'devices'>('energy');
  const [isMapZoomed, setIsMapZoomed] = useState(true);
  const currentRoom = ROOMS.find(r => r.id === selectedRoomId) || ROOMS[0];
  const currentSensors = MOCK_SENSORS[selectedRoomId] || [];
  // ─── Handlers ───
  const handleLogin = useCallback((token: string) => {
    setAuthToken(token);
    setCurrentView('main');
  }, []);
  const handleSelectRoom = useCallback((id: string) => {
    setSelectedRoomId(id);
  }, []);
  const handleSelectSensor = useCallback((sensor: Sensor) => {
    setSelectedSensor(sensor);
    setSlideDirection('left');
    setCurrentView('sensorDetail');
  }, []);
  const handleBackFromSensor = useCallback(() => {
    setSlideDirection('right');
    setCurrentView('main');
    setSelectedSensor(null);
  }, []);
  const handleOpenMap = useCallback(() => {
    setSlideDirection('left');
    setIsMapZoomed(true);
    setCurrentView('floorPlan');
  }, []);
  const handleCloseMap = useCallback(() => {
    setSlideDirection('right');
    setCurrentView('main');
  }, []);
  // ─── Login Screen ───
  if (!authToken || currentView === 'login') {
    return <LoginScreen onLogin={handleLogin} data-id="element-0" />;
  }
  // ─── Determine animation variants ───
  const getInitial = () => slideDirection === 'left' ? 'enterFromRight' : 'enterFromLeft';
  const getExit = () => slideDirection === 'left' ? 'exitToLeft' : 'exitToRight';
  return <div className="h-screen bg-[#0a0f1a] text-slate-200 flex flex-col overflow-hidden max-w-md mx-auto relative" data-id="element-1">
      {/* Background */}
      <div className="fixed inset-0 pointer-events-none z-0 opacity-10" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.03) 1px, transparent 1px)',
      backgroundSize: '32px 32px'
    }} data-id="element-2" />
      <div className="fixed inset-0 pointer-events-none z-0 bg-[radial-gradient(circle_at_center,_transparent_0%,_#0a0f1a_100%)]" data-id="element-3" />

      {/* Header - always visible */}
      <div className="relative z-20" data-id="element-4">
        {currentView === 'sensorDetail' && selectedSensor ? <div className="h-14 flex items-center px-4 bg-[#0a0f1a]/95 backdrop-blur-md border-b border-white/5" data-id="element-5">
            <button onClick={handleBackFromSensor} className="p-2 -ml-1 rounded-lg active:bg-white/5 transition-colors" data-id="element-6">
              <ArrowLeftIcon className="w-5 h-5 text-slate-400" data-id="element-7" />
            </button>
            <div className="flex-1 text-center" data-id="element-8">
              <h1 className="text-base font-bold text-slate-200" data-id="element-9">
                {selectedSensor.label}
              </h1>
              <p className="text-[10px] text-slate-500 font-mono" data-id="element-10">
                {selectedSensor.type === 'SEO' ? 'Smart Outlet' : selectedSensor.type === 'SEL' ? 'Smart Light' : selectedSensor.type === 'SES' ? 'Smart Switch' : selectedSensor.type === 'RADAR' ? 'Radar Sensor' : 'Sensor'}
              </p>
            </div>
            <div className="w-9" data-id="element-11" />
          </div> : currentView === 'floorPlan' ? <div className="h-14 flex items-center px-4 bg-[#0a0f1a]/95 backdrop-blur-md border-b border-white/5" data-id="element-12">
            <button onClick={handleCloseMap} className="p-2 -ml-1 rounded-lg active:bg-white/5 transition-colors" data-id="element-13">
              <ArrowLeftIcon className="w-5 h-5 text-slate-400" data-id="element-14" />
            </button>
            <div className="flex-1 text-center" data-id="element-15">
              <h1 className="text-base font-bold text-slate-200" data-id="element-16">평면도</h1>
            </div>
            <div className="w-9" data-id="element-17" />
          </div> : <MobileHeader rooms={ROOMS} selectedRoomId={selectedRoomId} onSelectRoom={handleSelectRoom} onOpenMap={handleOpenMap} onOpenAlerts={() => setShowAlerts(true)} hasAlerts={ROOMS.find(r => r.id === selectedRoomId)?.id === 'bedroom' || selectedRoomId === 'kitchen' || selectedRoomId === 'master_bedroom'} data-id="element-18" />}
      </div>

      {/* Main Content Area with transitions */}
      <div className="flex-1 relative z-10 overflow-hidden" data-id="element-19">
        <AnimatePresence mode="wait" initial={false} data-id="element-20">
          {currentView === 'main' && <motion.div key="main" variants={slideVariants} initial={getInitial()} animate="center" exit={getExit()} transition={{
          type: 'tween',
          duration: 0.25,
          ease: 'easeInOut'
        }} className="absolute inset-0" data-id="element-21">
              <SensorListView room={currentRoom} sensors={currentSensors} onSelectSensor={handleSelectSensor} data-id="element-22" />
            </motion.div>}

          {currentView === 'sensorDetail' && selectedSensor && <motion.div key="sensorDetail" variants={slideVariants} initial="enterFromRight" animate="center" exit="exitToRight" transition={{
          type: 'tween',
          duration: 0.25,
          ease: 'easeInOut'
        }} className="absolute inset-0 overflow-y-auto" data-id="element-23">
              <SensorDetailSidebar sensor={selectedSensor} onBack={handleBackFromSensor} onClose={handleBackFromSensor} data-id="element-24" />
            </motion.div>}

          {currentView === 'floorPlan' && <motion.div key="floorPlan" variants={slideVariants} initial="enterFromRight" animate="center" exit="exitToRight" transition={{
          type: 'tween',
          duration: 0.25,
          ease: 'easeInOut'
        }} className="absolute inset-0 flex flex-col" data-id="element-25">
              <div className="flex-1 flex items-center justify-center p-2 overflow-hidden" data-id="element-26">
                <FloorPlanHeatMap rooms={ROOMS} selectedRoomId={selectedRoomId} onSelectRoom={id => {
              handleSelectRoom(id);
              setIsMapZoomed(true);
            }} isZoomed={isMapZoomed} onZoomOut={() => setIsMapZoomed(false)} onSelectSensor={handleSelectSensor} viewMode={viewMode} zoneOverrides={INITIAL_EDITOR_ZONES.map(z => ({
              id: z.id,
              top: z.top,
              left: z.left,
              width: z.width,
              height: z.height
            }))} data-id="element-27" />
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Alerts Bottom Sheet */}
      <AnimatePresence data-id="element-28">
        {showAlerts && <AlertsSheet room={currentRoom} onClose={() => setShowAlerts(false)} data-id="element-29" />}
      </AnimatePresence>
    </div>;
}